import "./new.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import { FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, CircularProgress, Alert } from '@mui/material';
import { useEffect, useState, useContext } from "react";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  getDocs,
  where,
  query,
  updateDoc,
  arrayUnion
} from "firebase/firestore";
import { db, storage } from "../../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { uploadLogToFirestore, uploadErrorLogToFirestore } from "../../utils/logUtils";
import { useSidebarState } from "../../components/sidebar/SidebarStateContext";
import { fetchAdministrativosAsignados, fetchInspectoresAsignados } from "./utils/FirebaseUtils";
import EmpresaComponent from './utils/EmpresaComponent'; // Importamos el componente de empresa
import { comunidades, provincias } from "../../zoneData";
import noImage from '../../images/no-image.png';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { 
  registrarServicioEnFreematica, 
  verificarServicioExisteEnFreematica 
} from "../../components/cuadrantes/services/ApiMarcajes";

/**
 * Componente para crear una nueva tienda.
 *
 * @param {Object} inputs - Los campos de entrada para la tienda.
 * @param {string} title - El título del formulario.
 * @returns {JSX.Element} - Componente NewTienda.
 */
const NewClienteTienda = ({ inputs, title }) => {
  const { currentUser, currentService, adminData } = useContext(AuthContext);

  const [file, setFile] = useState("");
  const [data, setData] = useState({});
  const [per, setPerc] = useState(null);
  const [isDuplicateNumTienda, setIsDuplicateNumTienda] = useState(false);
  const [inspectoresAsignados, setInspectoresAsignados] = useState([]);
  const [administrativosAsignados, setAdministrativosAsignados] = useState([]);
  const [selectedInspectores, setSelectedInspectores] = useState([]);
  const [selectedAdministrativos, setSelectedAdministrativos] = useState([]);
  const [selectedValues, setSelectedValues] = useState({});
  const [adminProvincias, setAdminProvincias] = useState(adminData ? adminData.provincias || [] : []);
  const [adminComunidades, setAdminComunidades] = useState(adminData ? adminData.comunidades || [] : []);
  const [selectedEmpresa, setSelectedEmpresa] = useState(''); // Estado para la empresa seleccionada
  
  // Estados para registro en Freematica
  const [registrarEnFreematica, setRegistrarEnFreematica] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [freematicaStatus, setFreematicaStatus] = useState(null); // { type: 'success'|'error'|'warning', message: '' }

  const navigate = useNavigate();
  const { clienteId, nombreCliente } = useParams(); // Obtiene el clienteId de la URL
  const { collapsed } = useSidebarState();
  const containerClass = collapsed ? "newContainer collapsed" : "newContainer";


  useEffect(() => {
    // Comprobar si currentService está disponible
    if (currentService) {
      const currentServiceId = currentService.id;

      // Obtener inspectores asignados
      fetchInspectoresAsignados(currentServiceId).then((inspectores) => {
        setInspectoresAsignados(inspectores);
      });

      // Obtener administrativos asignados
      fetchAdministrativosAsignados(currentServiceId).then((administrativos) => {
        setAdministrativosAsignados(administrativos);
      });
    }
  }, [currentService]);

  useEffect(() => {
    const uploadFile = () => {
      const name = new Date().getTime() + file.name;

      const storageRef = ref(storage, file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
          setPerc(progress);
          switch (snapshot.state) {
            case "paused":
              console.log("Upload is paused");
              break;
            case "running":
              console.log("Upload is running");
              break;
            default:
              break;
          }
        },
        (error) => {
          uploadErrorLogToFirestore(error)
          console.log(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setData((prev) => ({ ...prev, img: downloadURL }));
          });
        }
      );
    };
    file && uploadFile();
  }, [file]);

  // Manejador para cuando cambia la empresa seleccionada
  const handleEmpresaChange = (empresaData) => {
    setData(prev => ({
      ...prev,
      empresaId: empresaData.empresaId,
      empresaNombre: empresaData.empresaNombre,
      codEmpresa: empresaData.codEmpresa
    }));
  };

  /**
 * Maneja el cambio de los campos de entrada.
 *
 * @param {Object} e - Evento del campo de entrada.
 */
  const handleInput = (e) => {
    const id = e.target.id || e.target.name; // Usar name como fallback si id no está disponible
    const isSelectMultiple = Array.isArray(e.target.value); // Verificar si es un Select múltiple
    const value = isSelectMultiple ? e.target.value : e.target.value;

    // Actualizar el estado de numTienda
    if (id === "numTienda") {
      setIsDuplicateNumTienda(false); // Reiniciar el estado de la comprobación
    }

    // Manejar los diferentes tipos de inputs
    switch (id) {
      case "inspectoresAsignados":
        setSelectedInspectores(value);
        break;

      case "administrativosAsignados":
        setSelectedAdministrativos(value);
        break;

      case "provincia":
      case "comunidad":
        // Solo actualizar si la provincia o comunidad está en las del admin
        if (adminProvincias.includes(value) || adminComunidades.includes(value)) {
          setData(prev => ({ ...prev, [id]: value }));
        }
        break;

      default:
        setData(prev => ({ ...prev, [id]: value }));
    }
  };


  /**
 * Maneja el registro de la nueva tienda.
 *
 * @param {Object} e - Evento del formulario.
 */
  const handleAdd = async (e) => {
    e.preventDefault();

    // Verificar que se ha seleccionado una empresa
    if (!data.empresaId) {
      alert("Debe seleccionar una empresa");
      return;
    }

    // Validar localización si se quiere registrar en Freematica
    if (registrarEnFreematica) {
      if (!data.localizacion) {
        alert("Debe introducir la localización (CONTRATO/SERVICIO) para registrar en Freematica");
        return;
      }
      
      // Validar formato de localización (debe contener "/" y tener formato CONTRATO/SERVICIO)
      const locParts = data.localizacion.split('/');
      if (locParts.length < 2 || !locParts[0].trim() || !locParts[1].trim()) {
        alert("El formato de localización debe ser CONTRATO/SERVICIO (ej: 13550/03).\n\nEl valor introducido no es válido: " + data.localizacion);
        return;
      }
      
      // Verificar que el contrato sea numérico
      if (!/^\d+$/.test(locParts[0].trim())) {
        alert("El código de CONTRATO debe ser numérico (ej: 13550).\n\nValor introducido: " + locParts[0]);
        return;
      }
    }

    setIsRegistering(true);
    setFreematicaStatus(null);

    try {
      // Referencia directa a la colección de tiendas del servicio actual
      const tiendasRef = collection(db, "servicios", currentService.id, "tiendas");

      // Verificar si el número de tienda ya está en uso
      const numTiendaQuery = query(tiendasRef, where("numTienda", "==", data.numTienda));
      const numTiendaSnapshot = await getDocs(numTiendaQuery);

      if (!numTiendaSnapshot.empty) {
        setIsDuplicateNumTienda(true);
        setIsRegistering(false);
        alert("El número de tienda ya está en uso. Por favor, elija otro.");
        return;
      }

      // Si se quiere registrar en Freematica, hacerlo primero
      let freematicaResult = null;
      if (registrarEnFreematica && data.localizacion) {
        try {
          console.log('🏢 Registrando servicio en Freematica...');
          
          // Verificar si ya existe
          const verificacion = await verificarServicioExisteEnFreematica(data.localizacion);
          
          if (verificacion.existe) {
            console.log('⚠️ El servicio ya existe en Freematica');
            setFreematicaStatus({
              type: 'warning',
              message: 'El servicio ya existía en Freematica. Se registrará solo en Sentry.'
            });
          } else {
            // Convertir fecha de dd/mm/yyyy a yyyy-mm-dd para Freematica
            let fechaAltaAPI = new Date().toISOString().split('T')[0];
            if (data.fechaAlta) {
              const partes = data.fechaAlta.split('/');
              if (partes.length === 3) {
                fechaAltaAPI = `${partes[2]}-${partes[1]}-${partes[0]}`;
              }
            }
            
            // Registrar el servicio en Freematica
            freematicaResult = await registrarServicioEnFreematica({
              localizacion: data.localizacion,
              nombre: data.nombreTienda || data.nombre || nombreCliente,
              provincia: data.provincia,
              direccion: data.direccion,
              fechaAlta: fechaAltaAPI
            }, {
              empresa: data.codEmpresa || '0001'
            });
            
            console.log('✅ Servicio registrado en Freematica:', freematicaResult);
            setFreematicaStatus({
              type: 'success',
              message: `Servicio registrado en Freematica: ${freematicaResult.localizacion}`
            });
          }
        } catch (freematicaError) {
          console.error('❌ Error al registrar en Freematica:', freematicaError);
          setFreematicaStatus({
            type: 'error',
            message: `Error al registrar en Freematica: ${freematicaError.message}. Se guardará solo en Sentry.`
          });
          // Continuar con el registro en Sentry aunque falle Freematica
        }
      }

      // Crear la nueva tienda en Sentry
      // Preparar datos para Firestore (evitar undefined)
      const tiendaData = {
        ...data,
        cliente: clienteId,
        nombreCliente: nombreCliente,
        // Guardar tanto los campos nuevos como los antiguos
        inspectoresAsignados: selectedInspectores,
        administrativosAsignados: selectedAdministrativos,
        inspectores: selectedInspectores, // Campo antiguo
        administrativos: selectedAdministrativos, // Campo antiguo
        timeStamp: serverTimestamp(),
        // Guardar info de Freematica si se registró (asegurar valores no undefined)
        registradoEnFreematica: registrarEnFreematica && freematicaResult?.success === true ? true : false,
      };
      
      // Solo agregar freematicaLocalizacion si tiene valor
      if (freematicaResult?.localizacion || data.localizacion) {
        tiendaData.freematicaLocalizacion = freematicaResult?.localizacion || data.localizacion;
      }
      
      const newTiendaRef = await addDoc(tiendasRef, tiendaData);

      // ID de la nueva tienda
      const newTiendaId = newTiendaRef.id;

      // Actualizar documentos de inspectores y administrativos asignados
      for (const inspectorId of selectedInspectores) {
        const inspectorRef = doc(db, 'servicios', currentService.id, 'admins', inspectorId);
        await updateDoc(inspectorRef, {
          tiendas: arrayUnion(newTiendaId),
        });
      }

      for (const administrativoId of selectedAdministrativos) {
        const administrativoRef = doc(db, 'servicios', currentService.id, 'admins', administrativoId);
        await updateDoc(administrativoRef, {
          tiendas: arrayUnion(newTiendaId),
        });
      }

      uploadLogToFirestore('tienda añadida' + (freematicaResult?.success ? ' (+ Freematica)' : ''));
      
      // Si hubo error en Freematica, mostrar mensaje antes de navegar
      if (freematicaStatus?.type === 'error') {
        setTimeout(() => navigate(-1), 2000);
      } else {
        navigate(-1);
      }

    } catch (err) {
      // Convertir error a string para evitar problemas con Firestore
      const errorMessage = err?.message || String(err);
      uploadErrorLogToFirestore(`Error al registrar tienda: ${errorMessage}`);
      console.error('Error al registrar tienda:', err);
      setFreematicaStatus({
        type: 'error',
        message: `Error al registrar: ${errorMessage}`
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const getInspectorNameById = (id) => {
    const inspector = inspectoresAsignados.find(ins => ins.id === id);
    return inspector ? inspector.apellido ? `${inspector.nombre} ${inspector.apellido}` : inspector.nombre : '';
  };

  const getAdministrativoNameById = (id) => {
    const administrativo = administrativosAsignados.find(adm => adm.id === id);
    return administrativo ? administrativo.apellido ? `${administrativo.nombre} ${administrativo.apellido}` : administrativo.nombre : '';
  };

  return (
    <div className="new">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        <div className="top">
          <h1>{title}</h1>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(-1)}
            style={{ marginLeft: '20px' }}
          >
            Volver
          </Button>
        </div>
        <div className="bottom">
          <div className="left">
            <img
              src={
                file
                  ? URL.createObjectURL(file)
                  : noImage
              }
              alt=""
            />
            <div className='upload-label'>
              <label htmlFor="file">
                <DriveFolderUploadOutlinedIcon className="icon" /> Subir imagen
              </label>
            </div>
          </div>
          <div className="right">
            <form onSubmit={handleAdd}>
              <div className="formInput">
                <input
                  type="file"
                  id="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  style={{ display: "none" }}
                />
              </div>

              {/* Componente selector de empresa */}
              <div className="formInput" style={{ marginBottom: '20px' }}>
                <EmpresaComponent
                  selectedEmpresa={selectedEmpresa}
                  setSelectedEmpresa={setSelectedEmpresa}
                  onEmpresaChange={handleEmpresaChange}
                />
              </div>

              {/* Opción para registrar en Freematica */}
              <div className="formInput" style={{ 
                marginBottom: '20px', 
                padding: '15px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={registrarEnFreematica}
                      onChange={(e) => setRegistrarEnFreematica(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <span style={{ fontWeight: 500 }}>
                      🏢 Registrar también en Freematica
                    </span>
                  }
                />
                {registrarEnFreematica && (
                  <div style={{ marginLeft: '32px', marginTop: '8px' }}>
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#666', 
                      margin: 0
                    }}>
                      Se creará el servicio en Freematica usando el campo <strong>Localización</strong>
                    </p>
                    <p style={{ 
                      fontSize: '11px', 
                      color: '#888', 
                      margin: '4px 0 0 0',
                      fontStyle: 'italic'
                    }}>
                      📋 Formato: <code style={{ backgroundColor: '#eee', padding: '2px 6px', borderRadius: '3px' }}>CONTRATO/SERVICIO</code> (ej: 13550/03)
                    </p>
                    {data.localizacion && !data.localizacion.includes('/') && (
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#d32f2f', 
                        margin: '8px 0 0 0',
                        fontWeight: 500
                      }}>
                        ⚠️ El formato introducido no es válido. Debe incluir "/" (ej: 13550/03)
                      </p>
                    )}
                    {data.localizacion && data.localizacion.includes('/') && (
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#2e7d32', 
                        margin: '8px 0 0 0',
                        fontWeight: 500
                      }}>
                        ✅ Formato válido: Contrato <strong>{data.localizacion.split('/')[0]}</strong>, Servicio <strong>{data.localizacion.split('/')[1]}</strong>
                      </p>
                    )}
                  </div>
                )}
                
                {/* Mostrar estado de Freematica */}
                {freematicaStatus && (
                  <Alert 
                    severity={freematicaStatus.type} 
                    style={{ marginTop: '10px' }}
                    onClose={() => setFreematicaStatus(null)}
                  >
                    {freematicaStatus.message}
                  </Alert>
                )}
              </div>
              
              <div className="inputPairs">
                {inputs.map((input) => (
                  <div className="formInput" key={input.id}>
                    <label>{input.label}</label>
                    {input.id === "inspectoresAsignados" && (
                      <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id="inspectoresAsignados-label">{input.label}</InputLabel>

                        <Select
                          id={input.id}
                          name="inspectoresAsignados"
                          multiple
                          value={selectedInspectores}
                          onChange={(e) => handleInput(e)}
                          input={<OutlinedInput id="select-inspectores" label="Inspectores" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((id) => (
                                <Chip key={id} label={getInspectorNameById(id)} />
                              ))}
                            </Box>
                          )}
                        >
                          {inspectoresAsignados.map((inspector) => (
                            <MenuItem key={inspector.id} value={inspector.id}>
                              {inspector.nombre} {inspector.apellido}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {input.id === "administrativosAsignados" && (
                      <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id="administrativosAsignados-label">{input.label}</InputLabel>
                        <Select
                          id={input.id}
                          name="administrativosAsignados"
                          multiple
                          value={selectedAdministrativos}
                          onChange={(e) => handleInput(e)}
                          input={<OutlinedInput id="select-administrativos" label="Administrativos" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((id) => (
                                <Chip key={id} label={getAdministrativoNameById(id)} />
                              ))}
                            </Box>
                          )}
                        >
                          {administrativosAsignados.map((administrativo) => (
                            <MenuItem key={administrativo.id} value={administrativo.id}>
                              {administrativo.nombre} {administrativo.apellido}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                    )}

                    {['comunidad', 'provincia'].includes(input.id) && input.type === 'dropdown' && (
                      <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id={`${input.id}-label`}>{input.label}</InputLabel>
                        <Select
                          id={input.id}
                          name={input.id}
                          value={data[input.id]}
                          onChange={(e) => handleInput(e)}
                          input={<OutlinedInput id={`select-${input.id}`} label={input.label} />}
                        >
                          {input.id === 'comunidad' ? (
                            adminComunidades.map((comunidad) => (
                              <MenuItem key={comunidad} value={comunidad}>
                                {comunidad}
                              </MenuItem>
                            ))
                          ) : (
                            adminProvincias.map((provincia) => (
                              <MenuItem key={provincia} value={provincia}>
                                {provincia}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    )}
                    {/* Campo de fecha con formato dd/mm/yyyy */}
                    {input.type === "date" && (
                      <input
                        id={input.id}
                        type="text"
                        placeholder={input.placeholder || "dd/mm/yyyy"}
                        value={data[input.id] || ''}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Auto-formatear mientras escribe
                          value = value.replace(/[^\d]/g, ''); // Solo números
                          if (value.length >= 2) {
                            value = value.slice(0, 2) + '/' + value.slice(2);
                          }
                          if (value.length >= 5) {
                            value = value.slice(0, 5) + '/' + value.slice(5);
                          }
                          if (value.length > 10) {
                            value = value.slice(0, 10);
                          }
                          setData(prev => ({ ...prev, [input.id]: value }));
                        }}
                        style={{
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    )}
                    {input.id !== "inspectoresAsignados" &&
                      input.id !== "administrativosAsignados" &&
                      input.type !== "dropdown" &&
                      input.type !== "date" && (
                        <input
                          id={input.id}
                          type={input.type}
                          placeholder={input.placeholder}
                          onChange={handleInput}
                        />
                      )}
                  </div>

                ))}

              </div>
              <div className="button-container">
                <button 
                  disabled={(per !== null && per < 100) || isRegistering} 
                  type="submit"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '10px' 
                  }}
                >
                  {isRegistering ? (
                    <>
                      <CircularProgress size={20} color="inherit" />
                      {registrarEnFreematica ? 'Registrando en Sentry y Freematica...' : 'Registrando...'}
                    </>
                  ) : (
                    registrarEnFreematica ? 'Registrar' : 'Registrar'
                  )}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewClienteTienda;